import { Injectable, inject } from '@angular/core';

import { OfferCategory, TemplateCategory } from '@cds/betting-offer';
import { FixtureViewGroupingConfiguration } from '@cds/betting-offer/grouping/fixture-view';
import { GridViewGroupingConfiguration } from '@cds/betting-offer/grouping/grid-view';
import { GridConfig, MarketGroupingConfig } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { usePreloaderData } from '@frontend/sports/common/core/utils/preloaded-data';
import { BaseCdsApi, BaseCdsApiFactory, CDS_API_FACTORY } from '@frontend/sports/content-distribution/feature';
import { WINDOW } from '@frontend/vanilla/core';
import { Observable } from 'rxjs';

export interface FixtureContextRequest {
    category?: string;
    sportId?: number;
    regionIds?: number[];
    competitionIds?: number[];
    virtualCompetitionIds?: number;
    virtualCompetitionGroupIds?: number;
}

@Injectable({ providedIn: 'root' })
export class OfferGroupingApi {
    private marketGroupingConfig = inject(MarketGroupingConfig);
    private gridConfig = inject(GridConfig);

    private cdsApi: BaseCdsApi;
    private readonly _window = inject(WINDOW);
    readonly preloaderService = this._window['MS2JS']?.GridGroupLoader;

    constructor() {
        const apiFactory = inject<BaseCdsApiFactory>(CDS_API_FACTORY);

        this.cdsApi = apiFactory({ endpoint: '/offer-grouping' });
    }

    getFixtureGrouping(sportId: number): Observable<FixtureViewGroupingConfiguration | undefined> {
        return this.cdsApi.get<FixtureViewGroupingConfiguration>(
            this.marketGroupingConfig.regionalisedGroupingEnabled ? '/v2/fixture-view/regional' : '/v2/fixture-view',
            {
                sportId,
            },
        );
    }

    getFixtureGroupingBatch(sportIds: number[]): Observable<FixtureViewGroupingConfiguration[] | undefined> {
        return this.cdsApi.get<FixtureViewGroupingConfiguration[]>(
            this.marketGroupingConfig.regionalisedGroupingEnabled ? '/v2/fixture-view/batch/regional' : '/v2/fixture-view/batch',
            {
                sportIds: sportIds.join(','),
            },
        );
    }

    @usePreloaderData({
        preloader: function (this: OfferGroupingApi) {
            return this.preloaderService;
        },
    })
    getGridGrouping(): Observable<GridViewGroupingConfiguration[] | undefined> {
        return this.cdsApi.get<GridViewGroupingConfiguration[]>(
            this.gridConfig.isRegionalisedGridEnabled ? '/grid-view/all/regional' : '/grid-view/all',
        );
    }

    getFixturesContext(request: FixtureContextRequest): Observable<TemplateCategory[] | undefined> {
        const regionIds: number[] = [];
        const tournamentIds: number[] = [];

        if (request.regionIds) {
            if (request.sportId === SportConstant.Tennis) {
                tournamentIds.push(...request.regionIds);
            } else {
                regionIds.push(...request.regionIds);
            }
        }

        return this.cdsApi.get<TemplateCategory[]>(`/non-gridable-categories`, {
            category: request.category || OfferCategory.NonGridable,
            sportId: request.sportId,
            regionIds: regionIds.join(','),
            tournamentIds: tournamentIds.join(','),
            competitionIds: (request.competitionIds ?? []).join(','),
            virtualCompetitionIds: request.virtualCompetitionIds,
            virtualCompetitionGroupIds: request.virtualCompetitionGroupIds,
        });
    }
}
